import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { downloadBlob } from "@/lib/csv";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg } from "@/lib/api";
import { PageHeader, Empty } from "./ui";
import { PRESETS, PeriodPicker, defaultCustom, fmtDay } from "./reports/Period";
import { Figures } from "./reports/Figures";
import { ParticipantsReport } from "./reports/ParticipantsReport";
import { Locality } from "./reports/Locality";
import { Origins } from "./reports/Origins";
import { Service } from "./reports/Service";

const TABS = [["indicadores", "Indicadores"], ["participantes", "Participantes"], ["localidade", "Localidade"], ["origem", "Origem"], ["atendimento", "Atendimento"]];

export default function Reports() {
  const { api } = useOutletContext();
  const [preset, setPreset] = useState("30d");
  const [custom, setCustom] = useState(defaultCustom);
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  const [start, end] = useMemo(() => (preset === "custom" ? custom : PRESETS.find((p) => p.id === preset).range()), [preset, custom]);

  useEffect(() => {
    if (!start || !end || start > end) return;
    setError("");
    api.get("/admin/reports", { params: { start, end } }).then((x) => setR(x.data)).catch((e) => setError(errMsg(e)));
  }, [api, start, end]);

  const label = r ? (r.start === r.end ? fmtDay(r.start) : `${fmtDay(r.start)} a ${fmtDay(r.end)}`) : "";
  const excel = async () => {
    try {
      const res = await api.get("/admin/reports/export.xlsx", { params: { start, end }, responseType: "blob" });
      downloadBlob(res.data, `relatorio_zap_iudp_${start}_${end}.xlsx`);
    } catch (e) { toast.error("Não foi possível gerar o Excel."); }
  };
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Relatórios" description={r ? `Dados reais registrados · ${label}` : "Dados reais registrados no ZAP IUDP."}>
        <PeriodPicker preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} />
      </PageHeader>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {error ? <Empty testId="reports-error">{error}</Empty> : r === null ? <Empty>Carregando…</Empty> : (
          <Tabs defaultValue="indicadores" className="max-w-6xl px-5 py-6 md:px-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-zap-mist p-1 sm:w-auto">
                {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`reports-tab-${v}`}>{l}</TabsTrigger>)}
              </TabsList>
              <button data-testid="reports-export-xlsx" onClick={excel}
                className="flex h-9 items-center gap-1.5 rounded-md border border-zap-deep px-3 text-sm font-medium text-zap-deep hover:bg-zap-mist">
                <FileSpreadsheet className="h-4 w-4" />Baixar Excel
              </button>
            </div>
            <TabsContent value="indicadores"><Figures r={r} /></TabsContent>
            <TabsContent value="participantes"><ParticipantsReport rows={r.participants} period={`${r.start}_${r.end}`} /></TabsContent>
            <TabsContent value="localidade"><Locality data={r.locality} period={`${r.start}_${r.end}`} /></TabsContent>
            <TabsContent value="origem"><Origins origins={r.origins} campaigns={r.campaigns} /></TabsContent>
            <TabsContent value="atendimento"><Service s={r.service} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
